const API_BASE = process.env.REACT_APP_BACKEND_URL;

// The backend runs on Render's free plan, which puts the service to sleep after 15 idle minutes (a keep-alive Worker pings it, but a ping
// can still be missed). The first request after a sleep waits for the whole wake-up, which can take over a minute, and an eager proxy can
// answer 502/503/504 while it is still coming up. So a GET waits long enough for a wake-up and is retried instead of failing the page.
// Only GETs: they are safe to repeat, unlike the POSTs below.
const ATTEMPT_TIMEOUT_MS = 75000;
const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 2000;
const RETRY_STATUSES = new Set([502, 503, 504]);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// AbortSignal.timeout is missing from older browsers, so build the same thing by hand.
function timeoutSignal(ms) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, clear: () => clearTimeout(timer) };
}

async function fetchAPI(endpoint) {
  let lastError;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    const { signal, clear } = timeoutSignal(ATTEMPT_TIMEOUT_MS);
    let res;
    try {
      res = await fetch(`${API_BASE}${endpoint}`, { signal });
    } catch (e) {
      // Network error or timeout: the server may simply still be waking up.
      lastError = e;
      if (attempt < MAX_ATTEMPTS) await sleep(RETRY_DELAY_MS * attempt);
      continue;
    } finally {
      clear();
    }
    if (res.ok) return res.json();
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    lastError = new Error(err.detail || `HTTP ${res.status}`);
    if (!RETRY_STATUSES.has(res.status)) throw lastError;
    if (attempt < MAX_ATTEMPTS) await sleep(RETRY_DELAY_MS * attempt);
  }
  throw lastError;
}

async function postAPI(endpoint, body) {
  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

export const api = {
  getIndices: () => fetchAPI('/api/market/indices'),
  getVix: () => fetchAPI('/api/market/vix'),
  getTotal3: () => fetchAPI('/api/market/total3'),
  getFearGreed: () => fetchAPI('/api/market/fear-greed'),
  getSectors: () => fetchAPI('/api/market/sectors'),
  getHistorical: (symbol) => fetchAPI(`/api/market/historical/${symbol}`),
  getFedRate: () => fetchAPI('/api/market/fed-rate'),
  getMarketCycle: (lang) => fetchAPI(`/api/market/cycle${lang === 'en' ? '?lang=en' : ''}`),
  getMarketEvents: () => fetchAPI('/api/market/events'),
  getServiceStatus: () => fetchAPI('/api/stock/service-status'),
  getStockFundamentals: (ticker) => fetchAPI(`/api/stock/fundamentals/${ticker}`),
  getAIAnalysis: async (ticker, lang, accessToken) => {
    // Logged-in admin sessions skip the token/payment gate server-side; send
    // the admin token along so the backend can recognize them.
    const adminToken = localStorage.getItem('admin_token');
    const res = await fetch(`${API_BASE}/api/stock/ai-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
      },
      body: JSON.stringify({ ticker, lang: lang || 'it', access_token: accessToken || null }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      const e = new Error(err.detail || `HTTP ${res.status}`);
      e.status = res.status;
      throw e;
    }
    return res.json();
  },
};
